/**
 * Onboarding Save Status — Sprint B.2.1
 * ─────────────────────────────────────
 * Read-only signal describing the most recent persistence attempt
 * performed by OnboardingProvider. Pure UX surface — does NOT alter
 * lifecycle, persistence behavior, or session state.
 *
 *   • `idle`   — no save has been observed yet this session
 *   • `saving` — a write is in-flight (debounced visual only)
 *   • `saved`  — last write completed; `lastSavedAt` populated
 *   • `error`  — last write threw naturally; `error` populated.
 *                Never set by artificial simulation.
 */

import { createContext, useContext } from "react";

export type OnboardingSaveStatusKind = "idle" | "saving" | "saved" | "error";

export interface OnboardingSaveStatus {
  kind: OnboardingSaveStatusKind;
  /** Epoch ms of last successful save, if any. */
  lastSavedAt: number | null;
  /** Last natural error message, if last attempt failed. */
  error: string | null;
}

export const IDLE_SAVE_STATUS: OnboardingSaveStatus = {
  kind: "idle",
  lastSavedAt: null,
  error: null,
};

export const OnboardingSaveStatusContext =
  createContext<OnboardingSaveStatus>(IDLE_SAVE_STATUS);

export function useOnboardingSaveStatus(): OnboardingSaveStatus {
  return useContext(OnboardingSaveStatusContext);
}

/** Pure display helper — exported for testability. */
export function describeSaveStatus(
  s: OnboardingSaveStatus,
  now: number = Date.now(),
): string {
  if (s.kind === "saving") return "Saving…";
  if (s.kind === "error") return "Save failed";
  if (s.kind === "saved" && s.lastSavedAt != null) {
    const elapsedMs = Math.max(0, now - s.lastSavedAt);
    const mins = Math.floor(elapsedMs / 60000);
    if (mins <= 0) return "Saved just now";
    if (mins === 1) return "Saved 1 minute ago";
    if (mins < 60) return `Saved ${mins} minutes ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs === 1) return "Saved 1 hour ago";
    return `Saved ${hrs} hours ago`;
  }
  return "Your progress is saved.";
}
